/**
 * UniversalConstellationGraph — Any node can be the center.
 *
 * Multi-layer concentric Cytoscape.js visualization:
 *   Ring 0 (center): Any entity (person, org, device, case)
 *   Ring 1: Direct connections (organizations, tenants)
 *   Ring 2: Federal regulators (2nd hop through orgs)
 *   Ring 3: State regulators
 *   Ring 4: Payers + Standards
 *
 * Key features:
 *   - Shared regulatory nodes appear ONCE with multiple edges
 *     (e.g., HIPAA connects to ABC, Sunrise, AND Bright Smiles)
 *   - Click any node to re-center the constellation on it
 *   - Edge colors match the relationship type
 *   - Organization nodes show the role label (Employee, Resident, Patient)
 *
 * DRR-075: Universal constellation visualization.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import cytoscape, { type Core, type NodeSingular } from 'cytoscape';
import {
  Badge,
  Box,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';

import type {
  UniversalConstellationData,
  ConstellationNode,
  ConstellationEdge,
} from './hooks/useUniversalConstellation';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Concentric ring values — higher = closer to center. */
const RING_VALUES: Record<string, number> = {
  center: 100,
  organization: 80,
  federal: 55,
  state: 35,
  payer: 20,
  standard: 20,
};

/** Node colors by role/jurisdiction. */
const NODE_COLORS: Record<string, string> = {
  center: '#7048e8',        // Purple — the focal entity
  organization: '#228be6',  // Blue — organizations/tenants
  federal: '#e03131',       // Red — federal regulators
  state: '#f08c00',         // Amber — state regulators
  payer: '#2f9e44',         // Green — payers
  standard: '#868e96',      // Gray — voluntary standards
};

const NODE_BORDER_COLORS: Record<string, string> = {
  center: '#5f3dc4',
  organization: '#1864ab',
  federal: '#c92a2a',
  state: '#e67700',
  payer: '#2b8a3e',
  standard: '#495057',
};

/** Edge colors by type. */
const EDGE_COLORS: Record<string, string> = {
  ASSIGNED_TO: '#40c057',   // Green — employment
  LOCATED_AT: '#fab005',    // Yellow — residence
  PARTICIPATES: '#7950f2',  // Purple — participation (patient)
  CONTAINS: '#868e96',      // Gray — containment
  CORRELATES: '#f06595',    // Pink — correlation
  REGULATED_BY: '#e03131',  // Red — mandatory regulation
  GOVERNED_BY: '#adb5bd',   // Light gray — voluntary
};

/** Legend entries. */
const ROLE_LEGEND = [
  { key: 'center', label: 'Center', color: '#7048e8' },
  { key: 'organization', label: 'Organization', color: '#228be6' },
  { key: 'federal', label: 'Federal', color: '#e03131' },
  { key: 'state', label: 'State', color: '#f08c00' },
  { key: 'payer', label: 'Payer', color: '#2f9e44' },
  { key: 'standard', label: 'Standard', color: '#868e96' },
];

const EDGE_LEGEND = [
  { key: 'ASSIGNED_TO', label: 'Works at', color: '#40c057' },
  { key: 'LOCATED_AT', label: 'Lives at', color: '#fab005' },
  { key: 'PARTICIPATES', label: 'Patient at', color: '#7950f2' },
  { key: 'REGULATED_BY', label: 'Regulated', color: '#e03131' },
  { key: 'GOVERNED_BY', label: 'Voluntary', color: '#adb5bd' },
];

// ---------------------------------------------------------------------------
// Cytoscape Stylesheet
// ---------------------------------------------------------------------------

const UNIVERSAL_STYLES = [
  // Base node
  {
    selector: 'node',
    style: {
      'background-color': '#228be6',
      'border-width': 2,
      'border-color': '#1864ab',
      label: 'data(label)',
      'text-valign': 'center',
      'text-halign': 'center',
      'font-size': 9,
      'font-weight': 'bold',
      color: '#fff',
      width: 60,
      height: 45,
      'text-wrap': 'wrap',
      'text-max-width': '75px',
    },
  },
  // Center node — largest, star shape
  {
    selector: 'node[role="center"]',
    style: {
      width: 100,
      height: 70,
      'font-size': 12,
      'border-width': 4,
      shape: 'round-rectangle',
      'text-max-width': '90px',
    },
  },
  // Organization nodes — barrel/rectangle
  {
    selector: 'node[role="organization"]',
    style: {
      width: 85,
      height: 55,
      'font-size': 10,
      'border-width': 3,
      shape: 'round-rectangle',
      'text-max-width': '80px',
    },
  },
  // Federal regulatory — diamond
  {
    selector: 'node[jurisdiction="federal"]',
    style: { shape: 'diamond', width: 60, height: 50 },
  },
  // State regulatory — hexagon
  {
    selector: 'node[jurisdiction="state"]',
    style: { shape: 'hexagon', width: 60, height: 50 },
  },
  // Payer — ellipse
  {
    selector: 'node[jurisdiction="payer"]',
    style: { shape: 'ellipse', width: 60, height: 48 },
  },
  // Standard — round-rectangle, dashed
  {
    selector: 'node[jurisdiction="standard"]',
    style: { shape: 'round-rectangle', width: 58, height: 45, 'border-style': 'dashed' },
  },
  // Shared regulatory nodes (connected to 2+ orgs) — thicker border
  {
    selector: 'node[?is_shared]',
    style: { 'border-width': 4 },
  },
  // Selection
  {
    selector: 'node:selected',
    style: {
      'border-width': 5,
      'border-color': '#f03e3e',
      'overlay-opacity': 0.15,
      'overlay-color': '#f03e3e',
    },
  },
  // Base edge
  {
    selector: 'edge',
    style: {
      width: 2,
      'line-color': '#adb5bd',
      'target-arrow-color': '#adb5bd',
      'target-arrow-shape': 'triangle',
      'curve-style': 'bezier',
      'arrow-scale': 0.9,
    },
  },
  // Center-to-org edges — thicker
  {
    selector: 'edge[ring="inner"]',
    style: { width: 3 },
  },
  // GOVERNED_BY — dashed
  {
    selector: 'edge[edge_type="GOVERNED_BY"]',
    style: {
      width: 1.5,
      'line-style': 'dashed',
      'line-color': '#adb5bd',
      'target-arrow-color': '#adb5bd',
    },
  },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type ConstellationLayoutMode = 'concentric' | 'mindmap';

export interface UniversalConstellationGraphProps {
  /** Constellation data from useUniversalConstellation hook. */
  constellation: UniversalConstellationData | null;
  /** Container height in pixels. */
  height?: number;
  /** Callback when user clicks a node (for re-centering). */
  onRecenter?: (nodeId: string) => void;
  /** Layout algorithm: concentric (rings) or mindmap (tree). */
  layoutMode?: ConstellationLayoutMode;
  /** Hide the built-in header/legend (for embedding in a modal with its own chrome). */
  minimal?: boolean;
}

// ---------------------------------------------------------------------------
// Selected node detail
// ---------------------------------------------------------------------------

interface SelectedDetail {
  nodeId: string;
  name: string;
  role: string;
  jurisdiction?: string;
  connectedVia?: string[];
  metadata?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UniversalConstellationGraph({
  constellation,
  height = 600,
  onRecenter,
  layoutMode = 'concentric',
  minimal = false,
}: UniversalConstellationGraphProps) {
  const cyRef = useRef<Core | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState<SelectedDetail | null>(null);

  // ---- Initialize Cytoscape -----------------------------------------------
  useEffect(() => {
    if (!containerRef.current || cyRef.current) return undefined;

    try {
      cyRef.current = cytoscape({
        container: containerRef.current,
        style: UNIVERSAL_STYLES as any,
        layout: { name: 'preset' },
        minZoom: 0.2,
        maxZoom: 3,
        wheelSensitivity: 1,
      });

      // Node tap
      cyRef.current.on('tap', 'node', (event) => {
        const node = event.target as NodeSingular;
        const nodeId = node.id();

        setSelectedDetail({
          nodeId,
          name: node.data('label'),
          role: node.data('role'),
          jurisdiction: node.data('jurisdiction') || undefined,
          connectedVia: node.data('connected_via') || undefined,
          metadata: node.data('metadata') || undefined,
        });
      });

      // Double-tap → re-center on that node
      cyRef.current.on('dbltap', 'node', (event) => {
        const node = event.target as NodeSingular;
        onRecenter?.(node.id());
      });

      // Background tap → deselect
      cyRef.current.on('tap', (event) => {
        if (event.target === cyRef.current) {
          setSelectedDetail(null);
        }
      });

      setIsInitialized(true);
    } catch (err) {
      console.error('Failed to initialize universal constellation:', err);
    }

    return () => {
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
    };
  }, [onRecenter]);

  // ---- Update graph when data changes -------------------------------------
  useEffect(() => {
    if (!cyRef.current || !constellation || !isInitialized) return;

    try {
      const cyNodes: cytoscape.ElementDefinition[] = [];
      const cyEdges: cytoscape.ElementDefinition[] = [];

      // Build nodes
      for (const node of constellation.nodes) {
        const colorKey = node.role === 'regulatory'
          ? (node.jurisdiction || 'federal')
          : node.role;

        const concentricValue = node.role === 'regulatory'
          ? (RING_VALUES[node.jurisdiction || 'federal'] || 20)
          : (RING_VALUES[node.role] || 20);

        // Access overlay: override border color based on access level metadata
        const accessLevel = node.metadata?.accessLevel;
        const borderColor = accessLevel === 'none'
          ? '#e03131'   // Red — fully restricted
          : accessLevel === 'partial'
          ? '#f08c00'   // Amber — partially restricted
          : accessLevel === 'full'
          ? '#2f9e44'   // Green — full access
          : NODE_BORDER_COLORS[colorKey] || '#1864ab';
        const borderWidth = accessLevel ? 4 : undefined;

        cyNodes.push({
          data: {
            id: node.nodeId,
            label: node.name,
            role: node.role,
            jurisdiction: node.jurisdiction || '',
            connected_via: node.connectedVia || [],
            is_shared: (node.connectedVia?.length || 0) > 1,
            metadata: node.metadata || {},
            concentric_value: concentricValue,
          },
          style: {
            'background-color': NODE_COLORS[colorKey] || '#228be6',
            'border-color': borderColor,
            ...(borderWidth ? { 'border-width': borderWidth } : {}),
          },
        });
      }

      // Build edges
      for (let i = 0; i < constellation.edges.length; i++) {
        const edge = constellation.edges[i];
        if (!edge) continue;
        const isInner = constellation.center.nodeId === edge.sourceId;
        const edgeColor = EDGE_COLORS[edge.edgeType] || '#adb5bd';

        cyEdges.push({
          data: {
            id: `edge-${i}`,
            source: edge.sourceId,
            target: edge.targetId,
            edge_type: edge.edgeType,
            label: edge.label,
            ring: isInner ? 'inner' : 'outer',
          },
          style: {
            'line-color': edgeColor,
            'target-arrow-color': edgeColor,
          },
        });
      }

      // Clear and add
      cyRef.current.elements().remove();
      cyRef.current.add(cyNodes);
      cyRef.current.add(cyEdges);

      // Layout: concentric (rings) or breadthfirst (mind-map tree)
      const layoutConfig =
        layoutMode === 'mindmap'
          ? {
              name: 'breadthfirst',
              directed: true,
              spacingFactor: 1.25,
              avoidOverlap: true,
              animate: true,
              animationDuration: 600,
              padding: 30,
              roots: `#${constellation.center.nodeId}`,
            }
          : {
              name: 'concentric',
              concentric: (node: NodeSingular) =>
                node.data('concentric_value') || 20,
              levelWidth: () => 1,
              minNodeSpacing: 30,
              animate: true,
              animationDuration: 600,
              padding: 25,
            };

      cyRef.current.layout(layoutConfig as any).run();
      cyRef.current.fit(undefined, 30);
    } catch (err) {
      console.error('Failed to update universal constellation:', err);
    }
  }, [constellation, isInitialized, layoutMode]);

  // ---- Fit-to-viewport ----------------------------------------------------
  const handleFitToView = useCallback(() => {
    cyRef.current?.fit(undefined, 30);
  }, []);

  // ---- Render --------------------------------------------------------------
  if (!constellation) {
    return (
      <Paper p="md" withBorder>
        <Stack align="center" gap="sm">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">Loading constellation...</Text>
        </Stack>
      </Paper>
    );
  }

  if (constellation.nodes.length <= 1) {
    return (
      <Paper p="md" withBorder>
        <Text size="sm" c="dimmed" ta="center">
          No connections found for {constellation.center.name}.
        </Text>
      </Paper>
    );
  }

  return (
    <Stack gap="sm">
      {!minimal && (
        <>
          {/* Header */}
          <Group justify="space-between">
            <Title order={5}>Entity Constellation</Title>
            <Group gap="xs">
              <Badge variant="filled" color="grape" size="sm">
                {constellation.center.name}
              </Badge>
              <Badge variant="light" color="blue" size="sm">
                {constellation.organizationCount} orgs
              </Badge>
              <Badge variant="light" color="red" size="sm">
                {constellation.uniqueRegulatoryCount} regulators
              </Badge>
              {constellation.sharedRegulatoryCount > 0 && (
                <Tooltip label="Regulatory bodies shared across multiple organizations">
                  <Badge variant="light" color="orange" size="sm">
                    {constellation.sharedRegulatoryCount} shared
                  </Badge>
                </Tooltip>
              )}
              <Badge
                variant="light"
                color="teal"
                size="sm"
                style={{ cursor: 'pointer' }}
                onClick={handleFitToView}
              >
                Fit
              </Badge>
            </Group>
          </Group>

          {/* Legend row 1: node roles */}
          <Group gap="xs">
            {ROLE_LEGEND.map((entry) => (
              <Badge key={entry.key} size="xs" variant="dot" color={entry.color}>
                {entry.label}
              </Badge>
            ))}
          </Group>

          {/* Legend row 2: edge types */}
          <Group gap="xs">
            {EDGE_LEGEND.map((entry) => (
              <Badge key={entry.key} size="xs" variant="outline" color={entry.color}>
                {entry.label}
              </Badge>
            ))}
            <Text size="xs" c="dimmed" fs="italic">
              Double-click any node to re-center
            </Text>
          </Group>
        </>
      )}

      {/* Cytoscape container */}
      <Box
        ref={containerRef}
        style={{
          width: '100%',
          height,
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: 'var(--mantine-radius-sm)',
          background: 'var(--mantine-color-gray-0)',
        }}
      />

      {/* Selected node detail */}
      {selectedDetail && (
        <Paper p="sm" withBorder>
          <Stack gap="xs">
            <Group gap="xs">
              <Text size="sm" fw={600}>Selected:</Text>
              <Badge
                variant="filled"
                color={NODE_COLORS[
                  selectedDetail.role === 'regulatory'
                    ? (selectedDetail.jurisdiction || 'federal')
                    : selectedDetail.role
                ] || 'blue'}
                size="sm"
              >
                {selectedDetail.name}
              </Badge>
              <Badge variant="light" color="gray" size="xs">
                {selectedDetail.role}
              </Badge>
              {selectedDetail.jurisdiction && (
                <Badge variant="light" color="gray" size="xs">
                  {selectedDetail.jurisdiction}
                </Badge>
              )}
            </Group>

            {selectedDetail.connectedVia && selectedDetail.connectedVia.length > 0 && (
              <Group gap={4}>
                <Text size="xs" c="dimmed">Connects through:</Text>
                {selectedDetail.connectedVia.map((orgId) => (
                  <Badge key={orgId} size="xs" variant="light" color="blue">
                    {orgId}
                  </Badge>
                ))}
                {selectedDetail.connectedVia.length > 1 && (
                  <Badge size="xs" variant="filled" color="orange">
                    Shared across {selectedDetail.connectedVia.length} orgs
                  </Badge>
                )}
              </Group>
            )}

            {selectedDetail.metadata?.roleLabel && (
              <Group gap={4}>
                <Text size="xs" c="dimmed">Role:</Text>
                <Text size="xs">{selectedDetail.metadata.roleLabel}</Text>
              </Group>
            )}

            {onRecenter && selectedDetail.role !== 'center' && (
              <Badge
                variant="light"
                color="grape"
                size="sm"
                style={{ cursor: 'pointer', alignSelf: 'flex-start' }}
                onClick={() => onRecenter(selectedDetail.nodeId)}
              >
                Re-center on {selectedDetail.name}
              </Badge>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
